import React from "react";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import TournamentTrophyIcon, { TOURNAMENT_HEADER_TROPHY_WIDTH } from "./TournamentTrophyIcon";
import {
  DIALOG_HEADER_CLOSE_OFFSET_PX,
  DIALOG_HEADER_CLOSE_SIZE_PX,
  DIALOG_HEADER_HEIGHT_PX,
} from "../../consts/dialog";

const combineClassNames = (...classes: (string | false | undefined)[]) => classes.filter(Boolean).join(" ");

export interface DialogToolbarProps {
  onClick: () => void;
  title?: string;
  subtitle?: string;
  statusLabel?: string;
  border?: boolean;
  secondary?: boolean;
  logo?: string;
  showTrophy?: boolean;
  titleId?: string;
}

const DialogToolbar: React.FC<DialogToolbarProps> = ({
  onClick,
  title,
  subtitle,
  statusLabel,
  logo,
  secondary,
  border = true,
  showTrophy = false,
  titleId,
}) => {
  const classes = useStyles();
  const hasTitleBlock = Boolean(title);
  const hasMeta = Boolean(subtitle || statusLabel);
  const chromeOnly = !title && !logo;

  return (
    <div
      className={combineClassNames(
        classes.root,
        hasTitleBlock && classes.rootTitled,
        hasTitleBlock && !hasMeta && classes.rootTitledCentered,
        !border && classes.rootBorderless,
        chromeOnly && classes.rootChromeOnly,
      )}
    >
      {hasTitleBlock ? (
        <div className={combineClassNames(classes.titleBlock, showTrophy && classes.titleBlockWithTrophy)}>
          {showTrophy ? (
            <div className={classes.titleTrophyWrap}>
              <TournamentTrophyIcon fillHeight />
            </div>
          ) : null}
          <div className={classes.titleText}>
            <Typography
              className={classes.title}
              color={secondary ? "secondary" : "inherit"}
              variant="subtitle1"
              component="h1"
              id={titleId}
              noWrap
            >
              {title}
            </Typography>
            {hasMeta ? (
              <div className={classes.metaRow}>
                {subtitle ? (
                  <Typography className={classes.subtitle} component="p" noWrap>
                    {subtitle}
                  </Typography>
                ) : null}
                {statusLabel ? (
                  <span
                    className={combineClassNames(
                      classes.statusTag,
                      statusLabel.toLowerCase().includes("final") ? classes.statusTagFinal : classes.statusTagLive,
                    )}
                  >
                    {statusLabel}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {logo ? <Avatar className={classes.avatar} alt="Club Logo" src={logo} /> : null}
      <IconButton
        onClick={onClick}
        disableRipple
        className={classes.closeButton}
        aria-label="Close dialog"
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default DialogToolbar;

const useStyles = makeStyles((theme: Theme) => {
  const closeTop = `max(${DIALOG_HEADER_CLOSE_OFFSET_PX}px, env(safe-area-inset-top, 0px))`;
  const closeRight = DIALOG_HEADER_CLOSE_OFFSET_PX;
  const headerPaddingRight = DIALOG_HEADER_CLOSE_SIZE_PX + DIALOG_HEADER_CLOSE_OFFSET_PX + 4;

  return createStyles({
    root: {
      position: "sticky",
      top: 0,
      zIndex: theme.zIndex.modal + 1,
      width: "100%",
      minWidth: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      backgroundColor: "#ffffff",
      color: "#1e293b",
      boxShadow: "inset 0 -1px 0 rgba(226, 232, 240, 0.92)",

      [theme.breakpoints.up("sm")]: {
        minWidth: "min(100%, 20rem)",
      },
    },
    rootTitled: {
      position: "relative",
      alignItems: "flex-start",
      justifyContent: "flex-start",
      minHeight: DIALOG_HEADER_HEIGHT_PX,
      paddingTop: closeTop,
      paddingRight: headerPaddingRight,
      paddingLeft: theme.spacing(2),
      paddingBottom: theme.spacing(1.5),
    },
    rootTitledCentered: {
      alignItems: "center",
      paddingBottom: closeTop,
    },
    rootBorderless: {
      boxShadow: "none",
    },
    rootChromeOnly: {
      position: "relative",
      minHeight: DIALOG_HEADER_HEIGHT_PX,
      justifyContent: "flex-end",
      paddingTop: closeTop,
      paddingRight: closeRight,
      paddingLeft: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    titleBlock: {
      flex: 1,
      minWidth: 0,
      paddingRight: theme.spacing(0.5),
    },
    titleBlockWithTrophy: {
      display: "flex",
      alignItems: "stretch",
      gap: theme.spacing(1.5),
    },
    titleTrophyWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: `0 0 ${TOURNAMENT_HEADER_TROPHY_WIDTH}px`,
      width: TOURNAMENT_HEADER_TROPHY_WIDTH,
      minHeight: 40,
      overflow: "hidden",
    },
    titleText: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minHeight: 40,
    },
    title: {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: "1.125rem",
      fontWeight: 600,
      margin: 0,
      textAlign: "left",
      letterSpacing: "-0.025em",
      lineHeight: 1.3,
      color: "#0f172a",

      [theme.breakpoints.down("sm")]: {
        fontSize: "1.0625rem",
      },
    },
    metaRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      gap: theme.spacing(0.5, 0.75),
      marginTop: theme.spacing(0.375),
      minWidth: 0,
      width: "100%",
      minHeight: 20,
    },
    subtitle: {
      margin: 0,
      flex: "1 1 auto",
      minWidth: 0,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontSize: "0.8125rem",
      fontWeight: 400,
      lineHeight: 1.4,
      color: "#64748b",
      letterSpacing: "0.01em",
    },
    statusTag: {
      flexShrink: 0,
      padding: "3px 8px",
      borderRadius: 4,
      fontSize: "0.625rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
    statusTagLive: {
      backgroundColor: "#22c55e",
      color: "#ffffff",
    },
    statusTagFinal: {
      backgroundColor: "#64748b",
      color: "#ffffff",
    },
    closeButton: {
      position: "absolute",
      top: closeTop,
      right: closeRight,
      width: DIALOG_HEADER_CLOSE_SIZE_PX,
      height: DIALOG_HEADER_CLOSE_SIZE_PX,
      padding: theme.spacing(1),
      color: "#64748b",
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      transition: theme.transitions.create(["transform", "color"], {
        duration: theme.transitions.duration.shortest,
      }),
      "&:hover": {
        backgroundColor: "rgba(241, 245, 249, 0.9)",
        color: "#334155",
      },
      "&.MuiIconButton-root:hover": {
        backgroundColor: "rgba(241, 245, 249, 0.9)",
      },
      "&:active": {
        transform: "scale(0.94)",
      },
    },
    avatar: {
      width: "6rem",
      height: "6rem",
      margin: "1rem auto 0",
    },
  });
});
